import {
  getPtaQueryParamOrSkip,
  useGetAllowedTokensParameterGroupQuery,
  useGetCustomerGroupParameterGroupQuery,
  useGetDomicileClassParameterGroupQuery,
  useGetProductGroupParameterGroupQuery,
  useGetSalesChannelParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type { FetchListParams } from 'Services/ProductManagement/types'
import { IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export const SELECTED_PARAMETER_KEY = 'selectedParameter'

function useParameterGroups(pta: string) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQueryParams, setSearchQueryParams] = useState<FetchListParams>({
    pta: searchParams.get('pta') ?? undefined
  })

  const selectedParameterQueryParam = searchParams.get(SELECTED_PARAMETER_KEY) ?? undefined
  const [selectedParameterId, setSelectedParameterId] = useState(selectedParameterQueryParam)

  const {
    data: productGroupParameterGroup = null,
    refetch: fetchProductGroups,
    isLoading: productGroupParameterGroupLoading,
    isFetching: productGroupParameterGroupFetching
  } = useGetProductGroupParameterGroupQuery(getPtaQueryParamOrSkip(pta))

  const {
    data: customerGroupParameterGroup = null,
    refetch: fetchCustomerGroups,
    isLoading: customerGroupParameterGroupLoading,
    isFetching: customerGroupParameterGroupFetching
  } = useGetCustomerGroupParameterGroupQuery(getPtaQueryParamOrSkip(pta))

  const {
    data: salesChannelParameterGroup = null,
    refetch: fetchSalesChannels,
    isLoading: salesChannelParameterGroupLoading,
    isFetching: salesChannelParameterGroupFetching
  } = useGetSalesChannelParameterGroupQuery(getPtaQueryParamOrSkip(pta))

  const {
    data: domicileClassParameterGroup = null,
    refetch: fetchDomicileClasses,
    isLoading: domicileClassParameterGroupLoading,
    isFetching: domicileClassParameterGroupFetching
  } = useGetDomicileClassParameterGroupQuery(getPtaQueryParamOrSkip(pta), {
    skip: IS_WALTTI_SERVICE_ENVIRONMENT // Waltti does not use domicile classes
  })

  const {
    data: allowedTokensParameterGroup = null,
    refetch: fetchAllowedTokens,
    isLoading: allowedTokensParameterGroupLoading,
    isFetching: allowedTokensParameterGroupFetching
  } = useGetAllowedTokensParameterGroupQuery(getPtaQueryParamOrSkip(pta))

  useEffect(() => {
    if (selectedParameterId) {
      selectedParameterId && searchParams.set(SELECTED_PARAMETER_KEY, selectedParameterId)
      setSearchParams(searchParams)
    } else {
      searchParams.delete(SELECTED_PARAMETER_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedParameterId])

  const setQueryParams = () => {
    Object.entries(searchQueryParams).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    setSearchParams(searchParams)
  }

  useEffect(() => {
    setQueryParams()
  }, [searchQueryParams])

  return {
    productGroupParameterGroup,
    isProductGroupParameterGroupLoading:
      productGroupParameterGroupLoading || productGroupParameterGroupFetching,
    customerGroupParameterGroup,
    isCustomerGroupParameterGroupLoading:
      customerGroupParameterGroupLoading || customerGroupParameterGroupFetching,
    salesChannelParameterGroup,
    isSalesChannelParameterGroupLoading:
      salesChannelParameterGroupLoading || salesChannelParameterGroupFetching,
    domicileClassParameterGroup,
    isDomicileClassParameterGroupLoading:
      domicileClassParameterGroupLoading || domicileClassParameterGroupFetching,
    allowedTokensParameterGroup,
    isAllowedTokensParameterGroupLoading:
      allowedTokensParameterGroupLoading || allowedTokensParameterGroupFetching,
    fetchProductGroups,
    fetchCustomerGroups,
    fetchSalesChannels,
    fetchDomicileClasses,
    fetchAllowedTokens,
    selectedParameterId,
    setSelectedParameterId,
    searchQueryParams,
    setSearchQueryParams
  }
}

export default useParameterGroups
